import React, { type ElementType } from 'react';

import RoadmapsIcon from '@atlaskit/icon-lab/core/roadmaps-plan';
import AppsIcon from '@atlaskit/icon/core/apps';
import AssetsGraph from '@atlaskit/icon-lab/core/assets-graph';
import ClockIcon from '@atlaskit/icon/core/clock';
import DashboardIcon from '@atlaskit/icon/core/dashboard';
import FilterIcon from '@atlaskit/icon/core/filter';
import GlobeIcon from '@atlaskit/icon/core/globe';
import GoalsIconOld from '@atlaskit/icon/core/goal';
import HeadphonesIcon from '@atlaskit/icon/core/headphones';
import PersonIcon from '@atlaskit/icon/core/person';
import {
	ConfluenceIcon,
	LoomIcon,
	TeamsIcon as NewTeamsIcon,
	ProjectsIcon as NewProjectsIcon,
	OpsgenieIcon,
	AssetsIcon,
	GoalsIcon,
} from '@atlaskit/logo';
import ProjectIcon from '@atlaskit/icon/core/project';
import SpacesIcon from '@atlaskit/icon-lab/core/spaces';
import PlansIcon from '@atlaskit/icon/core/roadmap';
import StarUnstarredIcon from '@atlaskit/icon/core/star-unstarred';
import TeamsIcon from '@atlaskit/icon/core/teams';
import Avatar from '@atlaskit/icon/core/person-avatar';
import ChartTrendUpIcon from '@atlaskit/icon/core/chart-trend-up';
import OperationsIcon from '@atlaskit/icon/core/operations';
import type { MessageDescriptor } from '@atlassian/jira-intl';
import {
	CONFIGURABLE_MENU_ID,
	GLOBAL_APP_MENU_ID,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import messages from '../messages.tsx';
import { type ConfigurableMenuId, L1_MENU_ID } from '../types.tsx';

export const PACKAGE_NAME = 'jiraNavigationAppsSidebarNav4Common';
export const TEAM_NAME = 'jira-cosmos';
export const MENU_GROUP_MIN_WIDTH = 250;

export const CONFIGURABLE_MENU_IDS = Object.values(CONFIGURABLE_MENU_ID);
export const CONFIGURABLE_MENU_IDS_AS_STRINGS: string[] = CONFIGURABLE_MENU_IDS;

export const GLOBAL_APP_MENU_IDS = Object.values(GLOBAL_APP_MENU_ID);
export const GLOBAL_APP_MENU_IDS_AS_STRINGS: string[] = GLOBAL_APP_MENU_IDS;

export const CONFIGURABLE_MESSAGE_AND_ICON_MAP_WITHOUT_PROJECT_TERM: {
	[K in ConfigurableMenuId]: { message: MessageDescriptor; Icon: ElementType };
} = {
	[L1_MENU_ID.APPS]: { message: messages.apps, Icon: AppsIcon },
	[L1_MENU_ID.GOALS]: { message: messages.goals, Icon: GoalsIconOld },
	[L1_MENU_ID.ROADMAPS]: { message: messages.roadmaps, Icon: RoadmapsIcon },
	[L1_MENU_ID.PLANS]: { message: messages.plans, Icon: PlansIcon },
	[L1_MENU_ID.PROJECTS]: { message: messages.projectsGalaxia, Icon: ProjectIcon },
	[L1_MENU_ID.FILTERS]: { message: messages.filters, Icon: FilterIcon },
	[L1_MENU_ID.DASHBOARDS]: { message: messages.dashboards, Icon: DashboardIcon },
	[L1_MENU_ID.OVERVIEWS]: { message: messages.overviews, Icon: GlobeIcon },
	[L1_MENU_ID.OPERATIONS]: { message: messages.operations, Icon: OperationsIcon },
	[L1_MENU_ID.ASSETS]: { message: messages.assets, Icon: AssetsGraph },
	[L1_MENU_ID.TEAMS]: { message: messages.teams, Icon: TeamsIcon },
	[L1_MENU_ID.RECENT]: { message: messages.recent, Icon: ClockIcon },
	[L1_MENU_ID.STARRED]: { message: messages.starred, Icon: StarUnstarredIcon },
	[L1_MENU_ID.CUSTOMER_SERVICE]: {
		message: messages.customerHubs,
		Icon: HeadphonesIcon,
	},
	[L1_MENU_ID.CUSTOMER_SERVICE_ANALYTICS]: {
		message: messages.customerServiceAnalytics,
		Icon: ChartTrendUpIcon,
	},
	[L1_MENU_ID.CUSTOMERS]: {
		message: messages.customers,
		Icon: PersonIcon,
	},
	[L1_MENU_ID.GLOBAL_APP_ASSETS]: {
		message: messages.assets,
		Icon: (props) => <AssetsIcon {...props} shouldUseNewLogoDesign size="xsmall" />,
	},
	[L1_MENU_ID.GLOBAL_APP_GOALS]: {
		message: messages.goals,
		Icon: (props) => <GoalsIcon {...props} shouldUseNewLogoDesign size="xsmall" />,
	},
	// App Shortcuts for Platform Component
	[L1_MENU_ID.GLOBAL_APP_CONFLUENCE]: {
		message: messages.confluence,
		Icon: (props) => <ConfluenceIcon {...props} shouldUseNewLogoDesign size="xsmall" />,
	},
	[L1_MENU_ID.GLOBAL_APP_LOOM]: {
		message: messages.loom,
		Icon: (props) => <LoomIcon {...props} shouldUseNewLogoDesign size="xsmall" />,
	},
	[L1_MENU_ID.GLOBAL_APP_PROJECTS]: {
		message: messages.projects,
		Icon: (props) => <NewProjectsIcon {...props} shouldUseNewLogoDesign size="xsmall" />,
	},
	[L1_MENU_ID.GLOBAL_APP_TEAMS]: {
		message: messages.teams,
		Icon: (props) => <NewTeamsIcon {...props} shouldUseNewLogoDesign size="xsmall" />,
	},
	[L1_MENU_ID.GLOBAL_APP_OPSGENIE]: {
		message: messages.opsgenie,
		Icon: (props) => <OpsgenieIcon {...props} shouldUseNewLogoDesign size="small" />,
	},
};

export const FOR_YOU_MESSAGE_AND_ICON_MAP: { message: MessageDescriptor; Icon: ElementType } = {
	message: messages.forYou,
	Icon: Avatar,
};

export const CONFIGURABLE_MESSAGE_AND_ICON_MAP_WITH_PROJECT_TERM: {
	[K in ConfigurableMenuId]: { message: MessageDescriptor; Icon: ElementType };
} = {
	[L1_MENU_ID.APPS]: { message: messages.apps, Icon: AppsIcon },
	[L1_MENU_ID.GOALS]: { message: messages.goals, Icon: GoalsIconOld },
	[L1_MENU_ID.ROADMAPS]: { message: messages.roadmaps, Icon: RoadmapsIcon },
	[L1_MENU_ID.PLANS]: { message: messages.plans, Icon: PlansIcon },
	[L1_MENU_ID.PROJECTS]: { message: messages.projects, Icon: ProjectIcon },
	[L1_MENU_ID.FILTERS]: { message: messages.filters, Icon: FilterIcon },
	[L1_MENU_ID.DASHBOARDS]: { message: messages.dashboards, Icon: DashboardIcon },
	[L1_MENU_ID.OVERVIEWS]: { message: messages.overviews, Icon: GlobeIcon },
	[L1_MENU_ID.OPERATIONS]: { message: messages.operations, Icon: OperationsIcon },
	[L1_MENU_ID.ASSETS]: { message: messages.assets, Icon: AssetsGraph },
	[L1_MENU_ID.TEAMS]: { message: messages.teams, Icon: TeamsIcon },
	[L1_MENU_ID.RECENT]: { message: messages.recent, Icon: ClockIcon },
	[L1_MENU_ID.STARRED]: { message: messages.starred, Icon: StarUnstarredIcon },
	[L1_MENU_ID.CUSTOMER_SERVICE]: {
		message: messages.customerHubs,
		Icon: HeadphonesIcon,
	},
	[L1_MENU_ID.CUSTOMER_SERVICE_ANALYTICS]: {
		message: messages.customerServiceAnalytics,
		Icon: ChartTrendUpIcon,
	},
	[L1_MENU_ID.CUSTOMERS]: {
		message: messages.customers,
		Icon: PersonIcon,
	},
	[L1_MENU_ID.GLOBAL_APP_ASSETS]: {
		message: messages.assets,
		Icon: (props) => <AssetsIcon {...props} shouldUseNewLogoDesign size="xsmall" />,
	},
	[L1_MENU_ID.GLOBAL_APP_GOALS]: {
		message: messages.goals,
		Icon: (props) => <GoalsIcon {...props} shouldUseNewLogoDesign size="xsmall" />,
	},
	// App Shortcuts for Platform Component
	[L1_MENU_ID.GLOBAL_APP_CONFLUENCE]: {
		message: messages.confluence,
		Icon: (props) => <ConfluenceIcon {...props} shouldUseNewLogoDesign size="xsmall" />,
	},
	[L1_MENU_ID.GLOBAL_APP_LOOM]: {
		message: messages.loom,
		Icon: (props) => <LoomIcon {...props} shouldUseNewLogoDesign size="xsmall" />,
	},
	[L1_MENU_ID.GLOBAL_APP_PROJECTS]: {
		message: messages.projects,
		Icon: (props) => <NewProjectsIcon {...props} shouldUseNewLogoDesign size="xsmall" />,
	},
	[L1_MENU_ID.GLOBAL_APP_TEAMS]: {
		message: messages.teams,
		Icon: (props) => <NewTeamsIcon {...props} shouldUseNewLogoDesign size="xsmall" />,
	},
	[L1_MENU_ID.GLOBAL_APP_OPSGENIE]: {
		message: messages.opsgenie,
		Icon: (props) => <OpsgenieIcon {...props} shouldUseNewLogoDesign size="small" />,
	},
};

export const SPACES_MESSAGE_AND_ICON_MAP_WITH_PROJECT_TERM: {
	message: MessageDescriptor;
	Icon: ElementType;
} = {
	message: messages.projects,
	Icon: SpacesIcon,
};

export const SPACES_MESSAGE_AND_ICON_MAP_WITHOUT_PROJECT_TERM: {
	message: MessageDescriptor;
	Icon: ElementType;
} = {
	message: messages.projectsGalaxia,
	Icon: SpacesIcon,
};

/**
 * To be used in examples, for example
 */
export const LEFT_SIDEBAR_EXPANDED_MIN_WIDTH = 240; // in pixels
export const LEFT_SIDEBAR_EXPANDED_DEFAULT_WIDTH = 320; // in pixels
export const IS_LEFT_SIDEBAR_COLLAPSED_DEFAULT = false;
export const TMP = 'TEAM_MANAGED_PROJECT';
export const CMP = 'COMPANY_MANAGED_PROJECT';

export const NAV4_OPT_OUT_FEEDBACK_GROUP_ID = 'nav4-opt-out';
export const NAV4_GENERAL_FEEDBACK_GROUP_ID = 'nav4-sidebar';

// used in jsw-expandable-project and board-create
export const NO_OF_BOARDS_TO_SHOW = 25;

export const FOR_YOU_MESSAGE_STRING: MessageDescriptor = messages.forYou;

export const CONFIGURABLE_MESSAGE_STRING_WITHOUT_PROJECT_TERM: {
	[K in ConfigurableMenuId]: MessageDescriptor;
} = {
	[L1_MENU_ID.APPS]: messages.apps,
	[L1_MENU_ID.GOALS]: messages.goals,
	[L1_MENU_ID.ROADMAPS]: messages.roadmaps,
	[L1_MENU_ID.PLANS]: messages.plans,
	[L1_MENU_ID.PROJECTS]: messages.projectsGalaxia,
	[L1_MENU_ID.FILTERS]: messages.filters,
	[L1_MENU_ID.DASHBOARDS]: messages.dashboards,
	[L1_MENU_ID.OVERVIEWS]: messages.overviews,
	[L1_MENU_ID.OPERATIONS]: messages.operations,
	[L1_MENU_ID.ASSETS]: messages.assets,
	[L1_MENU_ID.TEAMS]: messages.teams,
	[L1_MENU_ID.RECENT]: messages.recent,
	[L1_MENU_ID.STARRED]: messages.starred,
	[L1_MENU_ID.CUSTOMER_SERVICE]: messages.customerHubs,
	[L1_MENU_ID.CUSTOMER_SERVICE_ANALYTICS]: messages.customerServiceAnalytics,
	[L1_MENU_ID.CUSTOMERS]: messages.customers,
	[L1_MENU_ID.GLOBAL_APP_ASSETS]: messages.assets,
	[L1_MENU_ID.GLOBAL_APP_GOALS]: messages.goals,
	[L1_MENU_ID.GLOBAL_APP_CONFLUENCE]: messages.confluence,
	[L1_MENU_ID.GLOBAL_APP_LOOM]: messages.loom,
	[L1_MENU_ID.GLOBAL_APP_PROJECTS]: messages.projects,
	[L1_MENU_ID.GLOBAL_APP_TEAMS]: messages.teams,
	[L1_MENU_ID.GLOBAL_APP_OPSGENIE]: messages.opsgenie,
};

export const CONFIGURABLE_MESSAGE_STRING_WITH_PROJECT_TERM: {
	[K in ConfigurableMenuId]: MessageDescriptor;
} = {
	[L1_MENU_ID.APPS]: messages.apps,
	[L1_MENU_ID.GOALS]: messages.goals,
	[L1_MENU_ID.ROADMAPS]: messages.roadmaps,
	[L1_MENU_ID.PLANS]: messages.plans,
	[L1_MENU_ID.PROJECTS]: messages.projects,
	[L1_MENU_ID.FILTERS]: messages.filters,
	[L1_MENU_ID.DASHBOARDS]: messages.dashboards,
	[L1_MENU_ID.OVERVIEWS]: messages.overviews,
	[L1_MENU_ID.OPERATIONS]: messages.operations,
	[L1_MENU_ID.ASSETS]: messages.assets,
	[L1_MENU_ID.TEAMS]: messages.teams,
	[L1_MENU_ID.RECENT]: messages.recent,
	[L1_MENU_ID.STARRED]: messages.starred,
	[L1_MENU_ID.CUSTOMER_SERVICE]: messages.customerHubs,
	[L1_MENU_ID.CUSTOMER_SERVICE_ANALYTICS]: messages.customerServiceAnalytics,
	[L1_MENU_ID.CUSTOMERS]: messages.customers,
	[L1_MENU_ID.GLOBAL_APP_ASSETS]: messages.assets,
	[L1_MENU_ID.GLOBAL_APP_GOALS]: messages.goals,
	[L1_MENU_ID.GLOBAL_APP_CONFLUENCE]: messages.confluence,
	[L1_MENU_ID.GLOBAL_APP_LOOM]: messages.loom,
	[L1_MENU_ID.GLOBAL_APP_PROJECTS]: messages.projects,
	[L1_MENU_ID.GLOBAL_APP_TEAMS]: messages.teams,
	[L1_MENU_ID.GLOBAL_APP_OPSGENIE]: messages.opsgenie,
};
